// Corte Z del día: ventas, propinas y cierre formal.
//
// Rediseño 9-ago: la página ya no carga vacía — consulta sola el día de hoy
// al entrar y se actualiza al cambiar la fecha (antes había que escoger fecha
// y picarle a "Consultar" hasta para ver el día actual). La lógica y los
// endpoints (getCorte, cerrarCorte) no cambian.
import { useEffect, useState } from 'react';
import { cajaService } from '../services/api';
import ModalLlaveAcceso from '../components/ModalLlaveAcceso';
import { descargarBlob } from '../utils/descargar';
import { useMetodosPago } from '../hooks/useMetodosPago';
import { Printer, Download, KeyRound, CheckCircle2, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

function fmt(n: any) {
  return parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Suma días a una fecha YYYY-MM-DD sin sorpresas de zona horaria (T12:00).
function sumarDias(fecha: string, dias: number): string {
  const d = new Date(`${fecha}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}

function fechaBonita(fecha: string): string {
  return new Date(`${fecha}T12:00:00`).toLocaleDateString('es-MX', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}

const S = {
  card:     { background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--line-2)', boxShadow: '0 1px 3px rgba(0,0,0,.04)' } as React.CSSProperties,
  th:       { color: 'var(--text-muted)', fontWeight: 600, padding: '8px 0', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.04em' } as React.CSSProperties,
  etiqueta: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' } as React.CSSProperties,
  navBtn:   { width: 38, height: 38, borderRadius: 9, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' } as React.CSSProperties,
  chip:     { height: 38, padding: '0 14px', borderRadius: 9, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' } as React.CSSProperties,
};

export default function Corte() {
  const { label: metodoLabel, color: metodoColor } = useMetodosPago();
  const hoy = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy);
  const [corte, setCorte] = useState<any>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [cerrando, setCerrando] = useState(false);
  const [mensajeCierre, setMensajeCierre] = useState('');
  const [llaveCierre, setLlaveCierre] = useState(false);

  const consultar = async (f: string = fecha) => {
    setCargando(true);
    setError('');
    try {
      const res = await cajaService.getCorte(f);
      setCorte(res.data);
    } catch (err: any) {
      setCorte(null);
      setError(err.response?.data?.error || 'Error al obtener el corte');
    } finally {
      setCargando(false);
    }
  };

  // La página abre mostrando HOY y se refresca sola al cambiar la fecha:
  // el caso de todos los días (ver cómo va el día) es cero clics.
  useEffect(() => { consultar(fecha); setMensajeCierre(''); }, [fecha]); // eslint-disable-line react-hooks/exhaustive-deps

  const cerrarCorte = async () => {
    setCerrando(true);
    setMensajeCierre('');
    try {
      await (cajaService as any).cerrarCorte(fecha);
      setMensajeCierre('Corte cerrado correctamente');
      consultar(); // refrescar para mostrar estado
    } catch (err: any) {
      setMensajeCierre('' + (err.response?.data?.error || 'Error al cerrar el corte'));
    } finally {
      setCerrando(false);
    }
  };

  const exportarCSV = () => {
    if (!corte) return;
    const filas: any[][] = [
      ['Fecha', corte.fecha],
      [],
      ['Mesa', 'Mesero', 'Hora', 'Método', 'Cuenta', 'Propina', 'Total cobrado'],
      ...(corte.pagos || []).map((p: any) => [
        `Mesa ${p.mesa_numero}`,
        p.mesero,
        new Date(p.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
        metodoLabel(p.metodo),
        fmt(p.monto_total),
        fmt(p.propina),
        fmt(parseFloat(p.monto_total) + parseFloat(p.propina)),
      ]),
      [],
      ['', '', '', 'TOTAL', fmt(corte.total_ventas), fmt(corte.total_propinas),
        fmt(parseFloat(corte.total_ventas) + parseFloat(corte.total_propinas))],
    ];
    const csv = filas.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    descargarBlob(blob, `corte-${fecha}.csv`);
  };

  // Propinas agrupadas por mesero (para pagarles)
  const propinasMesero = (() => {
    const map: Record<string, { mesero: string; cuentas: number; propina: number }> = {};
    ((corte?.pagos) || []).forEach((p: any) => {
      const prop = parseFloat(p.propina) || 0;
      const nombre = p.mesero || 'Sin mesero';
      if (!map[nombre]) map[nombre] = { mesero: nombre, cuentas: 0, propina: 0 };
      map[nombre].cuentas += 1;
      map[nombre].propina += prop;
    });
    return Object.values(map).filter(m => m.propina > 0).sort((a, b) => b.propina - a.propina);
  })();
  const totalPropinas = propinasMesero.reduce((s, m) => s + m.propina, 0);

  const imprimirPropinas = () => {
    if (!propinasMesero.length) return;
    const filas = propinasMesero.map(m =>
      `<tr><td>${m.mesero}</td><td style="text-align:center">${m.cuentas}</td><td style="text-align:right">$${fmt(m.propina)}</td></tr>`
    ).join('');
    const totalCuentas = propinasMesero.reduce((s, m) => s + m.cuentas, 0);
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Propinas ${fecha}</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:12px;width:80mm;margin:0 auto;padding:8px;color:#000}
        h1{font-size:15px;text-align:center;margin-bottom:2px}
        .sub{text-align:center;font-size:11px;color:#444;margin-bottom:10px}
        table{width:100%;border-collapse:collapse}
        th{border-bottom:1px solid #000;padding:4px 2px;font-size:10px;text-transform:uppercase}
        td{padding:5px 2px;border-bottom:1px solid #eee;font-size:12px}
        tfoot td{border-top:1px solid #000;border-bottom:none;font-weight:bold;font-size:13px;padding-top:6px}
        .firma{margin-top:34px;border-top:1px solid #000;padding-top:4px;text-align:center;font-size:10px}
        @media print{@page{margin:5mm;size:80mm auto}}
      </style></head><body>
      <h1>PROPINAS A MESEROS</h1>
      <div class="sub">Corte del ${fecha}</div>
      <table>
        <thead><tr><th style="text-align:left">Mesero</th><th>Cuentas</th><th style="text-align:right">Propina</th></tr></thead>
        <tbody>${filas}</tbody>
        <tfoot><tr><td>TOTAL</td><td style="text-align:center">${totalCuentas}</td><td style="text-align:right">$${fmt(totalPropinas)}</td></tr></tfoot>
      </table>
      <div class="firma">_____________________________<br>Firma de quien entrega</div>
      <script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}</script>
      </body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:0';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument!;
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => {
      iframe.contentWindow!.focus();
      iframe.contentWindow!.print();
      setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 3000);
    }, 300);
  };

  const esHoy = fecha === hoy;

  return (
    <div style={{ maxWidth: 960 }}>
      {/* Encabezado + exportar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', color: 'var(--text)', margin: 0 }}>Corte de caja</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '3px 0 0', textTransform: 'capitalize' }}>{fechaBonita(fecha)}{esHoy ? ' · hoy' : ''}</p>
        </div>
        {corte && (
          <button
            onClick={exportarCSV}
            style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--line)', color: 'var(--text-2)', padding: '9px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600, background: 'var(--surface)', cursor: 'pointer' }}
          >
            <Download size={14} /> Exportar CSV
          </button>
        )}
      </div>

      {/* Barra de fecha: navegar días sin teclear */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22, flexWrap: 'wrap' }}>
        <button onClick={() => setFecha(f => sumarDias(f, -1))} style={S.navBtn} title="Día anterior">
          <ChevronLeft size={17} />
        </button>
        <input
          type="date"
          value={fecha}
          max={hoy}
          onChange={e => e.target.value && setFecha(e.target.value)}
          style={{ height: 38, border: '1px solid var(--line)', borderRadius: 9, padding: '0 12px', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit', outline: 'none', color: 'var(--text)' }}
        />
        <button
          onClick={() => setFecha(f => sumarDias(f, 1))}
          disabled={esHoy}
          style={{ ...S.navBtn, opacity: esHoy ? .4 : 1, cursor: esHoy ? 'not-allowed' : 'pointer' }}
          title="Día siguiente"
        >
          <ChevronRight size={17} />
        </button>
        <button onClick={() => setFecha(hoy)} style={{ ...S.chip, ...(esHoy ? { background: 'var(--strong)', color: 'var(--on-strong)', borderColor: 'var(--strong)' } : {}) }}>Hoy</button>
        <button onClick={() => setFecha(sumarDias(hoy, -1))} style={{ ...S.chip, ...(fecha === sumarDias(hoy, -1) ? { background: 'var(--strong)', color: 'var(--on-strong)', borderColor: 'var(--strong)' } : {}) }}>Ayer</button>
        <button onClick={() => consultar()} disabled={cargando} style={{ ...S.navBtn, marginLeft: 'auto', opacity: cargando ? .5 : 1 }} title="Actualizar">
          <RefreshCw size={15} style={cargando ? { animation: 'spin 1s linear infinite' } : undefined} />
        </button>
      </div>

      {error && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-line)', color: 'var(--brand-dark)', borderRadius: 12, padding: 16, marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {cargando && !corte && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--text-faint)', fontSize: 13 }}>Cargando corte…</div>
      )}

      {corte && (
        <>
          {/* Tarjetas resumen */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 14, marginBottom: 22 }}>
            <div style={{ ...S.card, padding: 16 }}>
              <p style={{ ...S.etiqueta, margin: '0 0 6px' }}>Total ventas</p>
              <p style={{ fontSize: 26, fontWeight: 800, color: 'var(--ok)', margin: 0 }}>${fmt(corte.total_ventas)}</p>
            </div>
            <div style={{ ...S.card, padding: 16 }}>
              <p style={{ ...S.etiqueta, margin: '0 0 6px' }}>Comandas</p>
              <p style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', margin: 0 }}>{corte.total_comandas}</p>
            </div>
            <div style={{ ...S.card, padding: 16 }}>
              <p style={{ ...S.etiqueta, margin: '0 0 6px' }}>Ticket promedio</p>
              <p style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', margin: 0 }}>${fmt(corte.ticket_promedio)}</p>
            </div>
            <div style={{ ...S.card, padding: 16 }}>
              <p style={{ ...S.etiqueta, margin: '0 0 6px' }}>Propinas</p>
              <p style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', margin: 0 }}>${fmt(corte.total_propinas)}</p>
            </div>
          </div>

          {/* Desglose por método con detalle de cada venta */}
          {corte.desglose_por_metodo.length === 0 ? (
            <div style={{ ...S.card, padding: 32, marginBottom: 16, textAlign: 'center' }}>
              <p style={{ color: 'var(--text-faint)', fontSize: 14, fontWeight: 600, margin: 0 }}>Sin ventas en esta fecha</p>
              <p style={{ color: 'var(--text-faint)', fontSize: 12.5, margin: '4px 0 0' }}>Prueba con otro día usando las flechas de arriba</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
              {corte.desglose_por_metodo.map((d: any) => {
                const ventasMetodo = (corte.pagos || []).filter((p: any) => p.metodo === d.metodo);
                const mc = { bg: metodoColor(d.metodo) + '22', text: metodoColor(d.metodo) };
                return (
                  <div key={d.metodo} style={{ ...S.card, overflow: 'hidden' }}>
                    {/* Encabezado del método */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', background: mc.bg, color: mc.text }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>
                        {metodoLabel(d.metodo)} — {d.comandas} {d.comandas === 1 ? 'venta' : 'ventas'}
                      </span>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>Subtotal: ${fmt(d.total)}</span>
                    </div>

                    {/* Detalle de cada venta */}
                    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--line-2)' }}>
                          <th style={{ ...S.th, textAlign: 'left', padding: '8px 24px' }}>Mesa</th>
                          <th style={{ ...S.th, textAlign: 'left' }}>Mesero</th>
                          <th style={{ ...S.th, textAlign: 'left' }}>Hora</th>
                          <th style={{ ...S.th, textAlign: 'right' }}>Cuenta</th>
                          <th style={{ ...S.th, textAlign: 'right' }}>Propina</th>
                          <th style={{ ...S.th, textAlign: 'right', padding: '8px 24px' }}>Total cobrado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ventasMetodo.map((p: any, i: number) => (
                          <tr key={p.id} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'var(--bg)' }}>
                            <td style={{ padding: '12px 24px', fontWeight: 600, color: 'var(--text)' }}>Mesa {p.mesa_numero}</td>
                            <td style={{ padding: '12px 0', color: 'var(--text-2)' }}>{p.mesero}</td>
                            <td style={{ padding: '12px 0', color: 'var(--text-faint)', fontSize: 12 }}>
                              {new Date(p.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td style={{ padding: '12px 0', textAlign: 'right', color: 'var(--text-2)' }}>${fmt(p.monto_total)}</td>
                            <td style={{ padding: '12px 0', textAlign: 'right', color: 'var(--text-muted)' }}>
                              {parseFloat(p.propina) > 0 ? `$${fmt(p.propina)}` : '—'}
                            </td>
                            <td style={{ padding: '12px 24px', textAlign: 'right', fontWeight: 700, color: 'var(--text)' }}>
                              ${fmt(parseFloat(p.monto_total) + parseFloat(p.propina))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '1px solid var(--line)', background: 'var(--bg)' }}>
                          <td colSpan={3} style={{ padding: '8px 24px', fontSize: 13, fontWeight: 700, color: 'var(--text-2)' }}>Totales {metodoLabel(d.metodo)}</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 700, color: 'var(--text-2)' }}>${fmt(d.total)}</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 700, color: 'var(--text-2)' }}>${fmt(d.propina)}</td>
                          <td style={{ padding: '8px 24px', textAlign: 'right', fontWeight: 700, color: 'var(--ok)' }}>
                            ${fmt(parseFloat(d.total) + parseFloat(d.propina))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })}

              {/* Gran total */}
              <div style={{ background: 'var(--brand-dark)', color: '#fff', borderRadius: 12, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700 }}>TOTAL DEL DÍA ({corte.total_comandas} comandas)</span>
                <span style={{ fontSize: 26, fontWeight: 800 }}>
                  ${fmt(parseFloat(corte.total_ventas) + parseFloat(corte.total_propinas))}
                </span>
              </div>
            </div>
          )}

          {/* Propinas por mesero */}
          {propinasMesero.length > 0 && (
            <div style={{ ...S.card, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', background: 'var(--bg)', borderBottom: '1px solid var(--line-2)' }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-2)' }}>Propinas por mesero</span>
                <button
                  onClick={imprimirPropinas}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--strong)', color: 'var(--on-strong)', fontSize: 12, fontWeight: 600, padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer' }}
                >
                  <Printer size={13} /> Imprimir propinas
                </button>
              </div>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--line-2)' }}>
                    <th style={{ ...S.th, textAlign: 'left', padding: '8px 24px' }}>Mesero</th>
                    <th style={{ ...S.th, textAlign: 'center' }}>Cuentas</th>
                    <th style={{ ...S.th, textAlign: 'right', padding: '8px 24px' }}>Propina a pagar</th>
                  </tr>
                </thead>
                <tbody>
                  {propinasMesero.map((m, i) => (
                    <tr key={m.mesero} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'var(--bg)' }}>
                      <td style={{ padding: '10px 24px', fontWeight: 600, color: 'var(--text-2)' }}>{m.mesero}</td>
                      <td style={{ padding: '10px 0', textAlign: 'center', color: 'var(--text-muted)' }}>{m.cuentas}</td>
                      <td style={{ padding: '10px 24px', textAlign: 'right', fontWeight: 700, color: 'var(--ok)' }}>${fmt(m.propina)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '1px solid var(--line)', background: 'var(--bg)' }}>
                    <td style={{ padding: '8px 24px', fontWeight: 700, color: 'var(--text-2)' }} colSpan={2}>Total propinas</td>
                    <td style={{ padding: '8px 24px', textAlign: 'right', fontWeight: 700, color: 'var(--ok)' }}>${fmt(totalPropinas)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Estado del corte formal */}
          {corte.corte_cerrado ? (
            <div style={{ background: 'var(--ok-bg)', border: '1px solid var(--ok-line)', borderRadius: 12, padding: '12px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ok)', fontSize: 13, fontWeight: 600 }}>
              <CheckCircle2 size={17} style={{ flexShrink: 0 }} />
              Corte cerrado el {new Date(corte.corte_cerrado.created_at).toLocaleString('es-MX')}
            </div>
          ) : (
            <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--warn-bg)', borderRadius: 12, padding: '12px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ color: 'var(--warn)', fontSize: 14, fontWeight: 700 }}>Este corte no ha sido cerrado formalmente</span>
              <button
                onClick={() => setLlaveCierre(true)}
                disabled={cerrando}
                style={{ background: 'var(--brand)', color: 'var(--on-brand)', padding: '9px 16px', borderRadius: 9, fontSize: 13, fontWeight: 700, border: 'none', cursor: cerrando ? 'not-allowed' : 'pointer', opacity: cerrando ? 0.4 : 1, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <KeyRound size={14} /> {cerrando ? 'Cerrando...' : 'Cerrar corte'}
              </button>
            </div>
          )}
          {mensajeCierre && (
            <p style={{ fontSize: 13, marginBottom: 16, color: 'var(--text-2)' }}>{mensajeCierre}</p>
          )}
        </>
      )}

      {llaveCierre && (
        <ModalLlaveAcceso
          permiso="puede_cerrar_turno"
          titulo="Cerrar corte de caja"
          descripcion={`Corte del ${fecha}. Esta acción quedará registrada en auditoría.`}
          onSuccess={() => { setLlaveCierre(false); cerrarCorte(); }}
          onCancel={() => setLlaveCierre(false)}
        />
      )}
    </div>
  );
}
